import { useEffect, useState } from "react";
import { AlarmClock, Check, CircleDashed, Skull } from "lucide-react";

import type { AutoReapStatus } from "@/api/types";

interface Props {
  status: AutoReapStatus;
}

/**
 * Operator-facing auto-reap panel rendered on the replica detail
 * card while the replica is in the `failed` / `shutting_down`
 * family. Three visual modes:
 *
 * 1. **Scheduled** — `scheduled_at` set and in the future. Renders a
 *    live `mm:ss` countdown so the operator can see exactly when the
 *    row will be terminated. The condition checklist below confirms
 *    *why* the schedule was stamped.
 * 2. **Reaping** — `scheduled_at` set and already past. The
 *    reconciler is about to (or just did) call `terminate_replica`.
 *    Holds the row visible so the operator can correlate the visual
 *    disappearance with the reap event.
 * 3. **Pending** — at least one eligibility condition is not met.
 *    Lists *which* condition(s) are blocking so the operator
 *    understands the row will hang around until the conditions
 *    converge.
 *
 * Countdown updates every second via a setInterval; on unmount the
 * timer is cleared.
 */
export function AutoReapPanel({ status }: Props) {
  const tick = useNowTick(status.scheduled_at != null);
  const scheduledMs = status.scheduled_at
    ? new Date(status.scheduled_at).getTime()
    : null;
  const remainingMs =
    scheduledMs != null ? Math.max(0, scheduledMs - tick) : null;
  const isScheduled = scheduledMs != null;
  const isReaping = isScheduled && remainingMs === 0;
  const metCount = status.conditions.filter((c) => c.met).length;
  const total = status.conditions.length;
  const variantClass = isReaping
    ? "auto-reap--reaping"
    : isScheduled
      ? "auto-reap--scheduled"
      : "";
  return (
    <div className={`auto-reap ${variantClass}`}>
      <div className="auto-reap__header">
        {isReaping ? (
          <>
            <Skull size={14} />
            <span>Reaping now</span>
          </>
        ) : isScheduled ? (
          <>
            <AlarmClock size={14} />
            <span>Auto-reap in</span>
            <span className="auto-reap__countdown">
              {formatRemaining(remainingMs ?? 0)}
            </span>
          </>
        ) : (
          <>
            <AlarmClock size={14} />
            <span>
              Auto-reap pending: {metCount}/{total} conditions met
            </span>
          </>
        )}
      </div>
      <p className="auto-reap__hint">
        {isReaping
          ? "Calling sky.serve.terminate_replica(purge=True). Once SkyServe stops reporting the replica, the row will be removed from the DB and a replica.reaped event will be logged."
          : isScheduled
            ? `Once the timer hits 0, the controller will call sky.serve.terminate_replica(purge=True). Schedule was stamped because all ${total} conditions below were observed simultaneously; if any flips before the timer expires the schedule will be cancelled.`
            : `Auto-reap will be scheduled once all ${total} conditions are met simultaneously (grace window: ${status.grace_minutes} min). Until then the row stays in place so you can inspect it.`}
      </p>
      <ul className="auto-reap__conditions">
        {status.conditions.map((c) => (
          <li
            key={c.id}
            className={`auto-reap__cond ${c.met ? "auto-reap__cond--met" : "auto-reap__cond--unmet"}`}
            title={c.label}
          >
            <span className="auto-reap__cond-mark" aria-hidden>
              {c.met ? <Check size={12} /> : <CircleDashed size={12} />}
            </span>
            <span>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function useNowTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [active]);
  return now;
}

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}
